/**
 * Email inbox — mail sent to service@airage.com, copied here by the mailbox.
 *
 * Inbox holds mail from real people; Filtered holds what rules, auto-reply
 * detection or spam checks set aside (kept 30 days) so nothing is lost
 * silently. Customer text is always rendered as React text.
 */
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api } from '../api/client'
import { PubBadge } from '../components/PubBadge'
import { ReplyPanel } from '../components/ReplyPanel'
import { InboundBlock } from '../components/InboundBlock'
import { AttachmentList } from '../components/AttachmentList'
import { fmtRelative } from '../lib/format'
import { filteredLabel, identityNote, senderLabel, shouldTryFiltered, type FilterRuleRef } from '../lib/emailInbox'
import { chipFor, type InboundMessage, type ReplyState } from '../lib/replies'
import { useRepliesEnabled } from '../lib/useRepliesEnabled'

type EmailAttachment = {
  n: number; name: string; size: number; content_type: string; available: boolean; skipped: string | null
}

type EmailMessage = {
  id: number
  publication: string | null
  from_email: string
  from_name: string | null
  subject: string | null
  body_text: string
  identity: 'verified' | 'unverified'
  filtered_reason: string | null
  filter_rule: FilterRuleRef | null
  status: string
  handled_at: string | null
  handled_by: string | null
  created_at: string | null
  attachments: EmailAttachment[]
  inbound: InboundMessage[]
  reply_state: ReplyState | null
}

type EmailMessageList = { items: EmailMessage[]; total: number; unread: number }

const PUBLICATIONS = [
  { value: '', label: 'No title' },
  { value: 'flight_journal', label: 'Flight Journal' },
  { value: 'model_airplane_news', label: 'Model Airplane News' },
  { value: 'rc_car_action', label: 'RC Car Action' },
  { value: 'diecast', label: 'Die Cast X' },
]

const STATUSES = [
  { value: 'new', label: 'Unread' },
  { value: 'read', label: 'In progress' },
  { value: 'archived', label: 'Archived' },
  { value: '', label: 'All' },
]

function errText(err: unknown): string {
  if (err instanceof ApiError) {
    const d = err.detail as { detail?: unknown } | null
    if (d && typeof d.detail === 'string') return d.detail
    return `That didn't work (error ${err.status}).`
  }
  return "That didn't work. Check your connection and try again."
}

export function EmailMessagesPage() {
  const queryClient = useQueryClient()
  // ?message=<id> (from an email link) opens that message with no status filter.
  const [search] = useSearchParams()
  const linked = Number(search.get('message')) || null
  const [tab, setTab] = useState<'inbox' | 'filtered'>('inbox')
  const [status, setStatus] = useState(linked ? '' : 'new')
  const [expanded, setExpanded] = useState<number | null>(linked)
  const replyV2 = useRepliesEnabled()
  // One error at a time, kept with the row it belongs to.
  const [actionError, setActionError] = useState<{ id: number; text: string } | null>(null)
  const [triedFiltered, setTriedFiltered] = useState(false)

  const params = new URLSearchParams({ tab })
  if (tab === 'inbox' && status) params.set('status', status)
  const qs = params.toString()

  const list = useQuery({
    queryKey: ['email-messages', tab, status],
    queryFn: () => api.get<EmailMessageList>(`/api/email-messages?${qs}`),
  })

  const items = list.data?.items ?? []

  useEffect(() => {
    if (shouldTryFiltered(linked, tab, !!list.data, items.map((m) => m.id), triedFiltered)) {
      setTriedFiltered(true)
      setTab('filtered')
    }
  })

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['email-messages'] })
    queryClient.invalidateQueries({ queryKey: ['navrail-email-unread'] })
    queryClient.invalidateQueries({ queryKey: ['support-inbox-filters'] })
  }

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: number; body: { status?: string; publication?: string | null } }) =>
      api.patch<EmailMessage>(`/api/email-messages/${id}`, body),
    onMutate: () => setActionError(null),
    onError: (err, { id }) => setActionError({ id, text: errText(err) }),
    onSuccess: refresh,
  })
  const moveToInbox = useMutation({
    mutationFn: (id: number) => api.post(`/api/email-messages/${id}/move-to-inbox`),
    onMutate: () => setActionError(null),
    onError: (err, id) => setActionError({ id, text: errText(err) }),
    onSuccess: refresh,
  })
  const ignoreSender = useMutation({
    mutationFn: (id: number) => api.post(`/api/email-messages/${id}/ignore-sender`),
    onMutate: () => setActionError(null),
    onError: (err, id) => setActionError({ id, text: errText(err) }),
    onSuccess: refresh,
  })


  return (
    <div className="p-8 space-y-6 max-w-[1200px]">
      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <div>
          <div className="label mb-1">Members · inbound</div>
          <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
            Email
            <span className="text-amber-glow"> ∙ </span>
            <span className="text-paper-trace">{list.data?.unread ?? 0} unread</span>
          </h1>
          <p className="mt-2 text-sm text-paper-dim max-w-[62ch]">
            Mail sent to service@airage.com, copied here by the mailbox. Filtered mail is kept 30 days.
          </p>
        </div>
        <Link to="/settings#support-inbox" className="label hover:underline">Filter rules</Link>
        <Link to="/help/reply-desk" className="label hover:underline self-end whitespace-nowrap">Reply desk guide</Link>
      </header>

      <div className="flex gap-4 border-b border-ink-500" role="tablist">
        {(['inbox', 'filtered'] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => { setTab(t); setExpanded(null); setActionError(null) }}
            className={`pb-2 font-mono text-[12px] uppercase tracking-label border-b-2 ${
              tab === t ? 'text-amber-glow border-amber-glow' : 'text-paper-trace border-transparent hover:text-paper'
            }`}
          >
            {t === 'inbox' ? 'Inbox' : 'Filtered'}
          </button>
        ))}
      </div>

      {tab === 'inbox' && (
        <div>
          <div className="label mb-1">Status</div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="bg-ink-900 border border-ink-500 px-3 py-2 text-paper font-mono text-[12px] min-w-[160px]"
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      )}

      {list.isLoading && <p className="text-paper-trace">Loading…</p>}
      {list.isError && <p className="text-red-400">Couldn't load messages. Try again.</p>}

      {!list.isLoading && items.length === 0 && (
        <p className="text-paper-trace">
          {tab === 'filtered'
            ? 'Nothing has been filtered.'
            : `Nothing here${status === 'new' ? ' — the queue is empty' : ''}.`}
        </p>
      )}

      <ul className="space-y-3">
        {items.map((m) => {
          const isOpen = expanded === m.id
          const note = identityNote(m.identity)
          return (
            <li key={m.id} className="border border-ink-500 rounded-sm">
              <button
                type="button"
                className="w-full text-left p-4 flex flex-wrap items-baseline gap-x-3 gap-y-1"
                onClick={() => { setExpanded(isOpen ? null : m.id); setActionError(null) }}
                aria-expanded={isOpen}
              >
                {m.status === 'new' && tab === 'inbox' && <span className="label-amber">NEW</span>}
                <span className="text-paper font-semibold">{senderLabel(m.from_name, m.from_email)}</span>
                <span className="text-paper-trace text-sm">{m.subject || '(no subject)'}</span>
                {m.publication
                  ? <PubBadge slug={m.publication} compact />
                  : <span className="label">No title</span>}
                {tab === 'filtered' && (
                  <span className="label">{filteredLabel(m.filtered_reason, m.filter_rule)}</span>
                )}
                {(() => { const c = chipFor(m.reply_state); return c && (
                  <span className={`font-mono text-[10px] uppercase tracking-label px-1.5 py-0.5 border rounded-sm ${c.cls}`}>{c.label}</span>
                ) })()}
                <span className="ml-auto text-paper-trace text-xs">
                  {m.created_at ? fmtRelative(m.created_at) : ''}
                </span>
              </button>

              {isOpen && (
                <div className="px-4 pb-4 space-y-3 border-t border-ink-500 pt-3">
                  <p className="text-sm text-paper-trace">
                    From <span className="font-mono text-paper">{m.from_email}</span>
                  </p>
                  <p className="text-paper whitespace-pre-wrap">{m.body_text}</p>
                  <AttachmentList ownerId={m.id} attachments={m.attachments} base="/api/inbound/email-attachments" />
                  {m.inbound.length > 0 && (
                    <ul className="space-y-2">
                      {m.inbound.map((i) => (
                        <li key={i.id}>
                          <InboundBlock item={i} attachmentBase="/api/inbound/attachments" />
                        </li>
                      ))}
                    </ul>
                  )}
                  {note && <p className="text-amber-glow text-sm">{note}</p>}
                  {m.handled_by && (
                    <p className="text-xs text-paper-trace">
                      {m.status} by {m.handled_by}
                      {m.handled_at ? ` · ${fmtRelative(m.handled_at)}` : ''}
                    </p>
                  )}

                  <label className="block">
                    <span className="label">Title</span>
                    <select
                      value={m.publication ?? ''}
                      disabled={patch.isPending}
                      onChange={(e) => patch.mutate({ id: m.id, body: { publication: e.target.value || null } })}
                      className="mt-1 block bg-ink-900 border border-ink-500 px-3 py-2 text-paper font-mono text-[12px] min-w-[220px]"
                    >
                      {PUBLICATIONS.map((p) => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </label>

                  {tab === 'inbox' && (replyV2.data === true
                    ? <ReplyPanel channel="email" messageId={m.id} />
                    : <p className="text-sm text-paper-trace">
                        Turn on drafted replies to answer from here; until then answer from service@.
                      </p>)}

                  {actionError?.id === m.id && (
                    <p className="text-red-400 text-sm">{actionError.text}</p>
                  )}
                  <div className="flex flex-wrap gap-2 pt-1">
                    {tab === 'filtered' && (
                      <button
                        type="button"
                        className="label-amber hover:underline"
                        disabled={moveToInbox.isPending}
                        onClick={() => moveToInbox.mutate(m.id)}
                      >
                        Move to inbox
                      </button>
                    )}
                    {tab === 'inbox' && (
                      <button
                        type="button"
                        className="label hover:underline"
                        disabled={ignoreSender.isPending}
                        onClick={() => {
                          if (window.confirm(`Ignore all mail from ${m.from_email}?`)) ignoreSender.mutate(m.id)
                        }}
                      >
                        Ignore sender
                      </button>
                    )}
                    {tab === 'inbox' && STATUSES.filter((s) => s.value && s.value !== m.status).map((s) => (
                      <button
                        key={s.value}
                        type="button"
                        className="label hover:underline"
                        disabled={patch.isPending}
                        onClick={() => patch.mutate({ id: m.id, body: { status: s.value } })}
                      >
                        Mark {s.label.toLowerCase()}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
